import { Link, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { getAuctionManagerPath } from '../../../utils/organization';
import { getAuctionManagerPath as getCommunityAuctionManagerPath } from '../../../utils/community';
import { getAuctionManagerPath as getClubAuctionManagerPath } from '../../../utils/club';
import AuctionModuleCard from '../components/AuctionModuleCard';
import { useAuth } from '../../../context/AuthContext';
import { fetchAuctionMode, resetAuctionForModeChange, saveAuctionMode } from '../../../services/tournamentService';
import PageShell from '../../../components/layout/PageShell';
import { FeatureBadgeSlot } from '../../commercial/FeatureExperience';

const buildAuctionModules = (baseAuctionPath) => [
  {
    id: 'online',
    title: 'Online Auction',
    description:
      'Set up digital bidding sessions with configurable rules, reserve prices, and transparent timelines for every squad.',
    actions: [
      { label: 'Edit auction', to: `${baseAuctionPath}/online`, variant: 'primary' },
      { label: 'Start auction', to: `${baseAuctionPath}/online/start/shuffle`, variant: 'outline-primary' },
    ],
    bullets: [
      'Publish auction catalogs with player bios, base prices, and team fit notes.',
      'Automate bidding windows, increments, and approvals to keep things moving.',
      'Export bidding summaries to share with coaches and owners after each round.',
    ],
  },
  {
    id: 'live',
    title: 'Live Auction',
    description:
      'Host an in-person auction experience with fast bidding controls and on-stage visibility.',
    actions: [
      { label: 'Edit live auction', to: `${baseAuctionPath}/live`, variant: 'primary' },
      { label: 'Start live auction', to: `${baseAuctionPath}/live/start/shuffle`, variant: 'outline-primary' },
      { label: 'Operator console', to: `${baseAuctionPath}/live/operator`, variant: 'outline-dark' },
    ],
    bullets: [
      'Run live bidding rounds with a dedicated auction desk experience.',
      'Surface active player details, current bids, and time remaining to all viewers.',
      'Keep auction managers aligned with clear team and budget visibility.',
    ],
  },
  {
    id: 'manual',
    title: 'Teams selected by auction manager it self',
    description:
      'Give auction managers the authority to finalize rosters, override bids, and lock in squads before publishing results.',
    actions: [{ label: 'Set teams', to: `${baseAuctionPath}/teams`, variant: 'primary' }],
    bullets: [
      'Approve or adjust winning bids and assign players to preferred rosters.',
      'Validate roster balance rules (roles, caps, overseas limits) before finalizing.',
      'Publish the official auction outcomes to teams and tournament administrators.',
    ],
  },
];

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const AuctionManagementPage = () => {
  const { token } = useAuth();
  const { organizationId, communityId, clubId, auctionManagerId, tournamentId } = useParams();
  const isIndividualRoute = Boolean(tournamentId && !organizationId && !communityId && !clubId);
  const baseAuctionPath =
    (isIndividualRoute
      ? `/individual-tournament/${tournamentId}/auction`
      : communityId
        ? getCommunityAuctionManagerPath(communityId, auctionManagerId, tournamentId)
        : clubId
          ? getClubAuctionManagerPath(clubId, auctionManagerId, tournamentId)
          : getAuctionManagerPath(organizationId, auctionManagerId, tournamentId)) || '/management/auction';
  const managementHubPath =
    isIndividualRoute
      ? `/individual-tournament/${tournamentId}/admin`
      : communityId && tournamentId
      ? `/community/${communityId}/managers/tournaments/${tournamentId}`
      : clubId && tournamentId
        ? `/club/${clubId}/managers/tournaments/${tournamentId}`
        : organizationId && tournamentId
          ? `/org/${organizationId}/managers/tournaments/${tournamentId}`
          : '/management';


  useEffect(() => {
    if (!isIndividualRoute || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('activeContext', JSON.stringify({
        type: 'individual',
        entityType: '',
        entityId: '',
      }));
    } catch (_error) {
      // no-op
    }
  }, [isIndividualRoute]);

  useEffect(() => {
    if (!tournamentId) return;
  }, [isIndividualRoute, tournamentId, organizationId, communityId, clubId]);
  const auctionModules = useMemo(() => buildAuctionModules(baseAuctionPath), [baseAuctionPath]);
  const [auctionMode, setAuctionMode] = useState(null);
  const [isSavingMode, setIsSavingMode] = useState(false);
  const [modeError, setModeError] = useState('');
  const [modeStatus, setModeStatus] = useState('');
  const [auctionStatus, setAuctionStatus] = useState('not_started');
  const [pendingMode, setPendingMode] = useState(null);
  const auctionModeOptions = [
    { id: 'online', label: 'Online auction', description: 'Run a fully digital auction experience.' },
    { id: 'live', label: 'Live auction', description: 'Run an in-person auction experience.' },
    { id: 'manual', label: 'Teams selected by auction manager', description: 'Finalize teams without bidding.' },
  ];

  useEffect(() => {
    const loadAuctionMode = async () => {
      if (!token || !tournamentId) return;
      setModeError('');

      try {
        const response = await fetchAuctionMode(tournamentId, token);
        setAuctionMode(response.auctionMode || null);
        setAuctionStatus(response.auctionStatus || 'not_started');
      } catch (error) {
        setModeError(error.message || 'Unable to load auction mode.');
      }
    };

    loadAuctionMode();
  }, [token, tournamentId]);

  const updateAuctionMode = async (nextMode) => {
    if (!token || !tournamentId) return;

    setIsSavingMode(true);
    setModeError('');
    setModeStatus('');

    try {
      const response = await saveAuctionMode(tournamentId, { auctionMode: nextMode }, token);
      setAuctionMode(response.auctionMode || null);
      setAuctionStatus(response.auctionStatus || 'not_started');
      setModeStatus(nextMode ? 'Auction mode saved.' : 'Auction mode reset.');
    } catch (error) {
      setModeError(error.message || 'Unable to save auction mode.');
    } finally {
      setIsSavingMode(false);
    }
  };


  const requestAuctionModeChange = (nextMode) => {
    if (nextMode === auctionMode) return;
    if (!auctionMode) {
      updateAuctionMode(nextMode);
      return;
    }
    setModeError('');
    setModeStatus('');
    setPendingMode(nextMode);
  };

  const cancelAuctionModeChange = () => {
    setPendingMode(null);
  };

  const confirmAuctionModeChange = async () => {
    if (!pendingMode || !token || !tournamentId || pendingMode === auctionMode) {
      setPendingMode(null);
      return;
    }

    setIsSavingMode(true);
    setModeError('');
    setModeStatus('');

    try {
      const response = await resetAuctionForModeChange(
        tournamentId,
        { oldMode: auctionMode, newMode: pendingMode },
        token,
      );
      setAuctionMode(response.auctionMode || null);
      setAuctionStatus(response.auctionStatus || 'not_started');
      setModeStatus('Auction mode changed and auction progress reset.');
      setPendingMode(null);
    } catch (error) {
      setModeError(error.message || 'Unable to change auction mode.');
    } finally {
      setIsSavingMode(false);
    }
  };

  const handleResetMode = () => {
    if (!auctionMode) return;
    const shouldReset = window.confirm(
      'Resetting the auction mode will clear saved auction settings, shuffles, and teams for this tournament. Continue?',
    );
    if (shouldReset) {
      updateAuctionMode(null);
    }
  };

  return (
    <>
      <div className="py-4 d-grid gap-4 auction-management-page">
        <div className="card border-0 shadow-sm overflow-hidden auction-management-hero">
          <div className="card-body d-flex flex-column flex-lg-row justify-content-between align-items-start gap-3">
            <div className="d-flex flex-column gap-2">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <p className="eyebrow text-uppercase text-primary fw-semibold mb-0">Management workspace</p>
                <span className="badge bg-warning-subtle text-warning-emphasis">In preview</span>
              </div>
              <h1 className="h3 mb-1">Auction Management</h1>
              <p className="text-muted mb-0">
                Explore the upcoming auction toolkit built for tournament control rooms. Plan online or live events,
                keep bidders in sync, and finalize squads with confidence.
              </p>
              <div className="d-flex flex-wrap gap-2">
                <span className="auction-pill">Online and hybrid formats</span>
                <span className="auction-pill">Real-time visibility</span>
                <span className="auction-pill">Roster governance</span>
              </div>
            </div>
            <div className="d-flex flex-column flex-lg-row gap-2 align-self-stretch">
              <Link to={managementHubPath} className="btn btn-outline-primary w-100">
                Back to Management Hub
              </Link>
              <button type="button" className="btn btn-primary w-100" disabled>
                Launch preview
              </button>
            </div>
          </div>
        </div>
        <div className="card border-0 shadow-sm">
          <div className="card-body d-flex flex-column gap-3">
            <div>
              <h2 className="h5 mb-1">Select auction mode</h2>
              <p className="text-muted mb-0">
                Choose one auction mode for this tournament. Changing it later requires confirmation and resets auction progress.
              </p>
            </div>
            {auctionStatus !== 'not_started' ? (
              <div className="alert alert-warning mb-0">
                Changing auction mode later will reset auction progress.
              </div>
            ) : null}
            {modeError ? <div className="alert alert-warning mb-0">{modeError}</div> : null}
            {modeStatus ? <div className="alert alert-success mb-0">{modeStatus}</div> : null}
            <div className="row g-3">
              {auctionModeOptions.map((option) => {
                const isSelected = auctionMode === option.id;
                const isDisabled = false;
                return (
                  <div className="col-12 col-lg-4" key={option.id}>
                    <div
                      className={`border rounded-3 p-3 h-100 d-flex flex-column gap-2 ${
                        isSelected ? 'border-primary bg-primary-subtle' : 'border-light'
                      }`}
                    >
                      <div className="d-flex align-items-start gap-2">
                        <input
                          type="radio"
                          name="auction-mode"
                          className="form-check-input mt-1"
                          checked={isSelected}
                          disabled={isSavingMode}
                          onChange={() => requestAuctionModeChange(option.id)}
                        />
                        <div>
                          <h3 className="h6 mb-1">{option.label}</h3>
                          <p className="text-muted mb-0">{option.description}</p>
                        </div>
                      </div>
                      {isDisabled ? (
                        <span className="badge bg-secondary-subtle text-secondary-emphasis align-self-start">
                          Change requires reset
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
            <div>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleResetMode}
                disabled={!auctionMode || isSavingMode}
              >
                Reset selection
              </button>
            </div>
          </div>
        </div>
      </div>

      {pendingMode ? (
        <>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow">
              <div className="modal-header">
                <h2 className="modal-title h5">Change Auction Mode?</h2>
                <button type="button" className="btn-close" aria-label="Cancel" onClick={cancelAuctionModeChange} />
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  Changing the auction mode will reset the current auction setup, bids, allocations, and auction progress.
                  <br />
                  This action cannot be undone.
                  <br />
                  Do you want to continue?
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={cancelAuctionModeChange} disabled={isSavingMode}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmAuctionModeChange} disabled={isSavingMode}>
                  {isSavingMode ? 'Changing...' : 'Change Mode'}
                </button>
              </div>
            </div>
          </div>
          </div>
          <div className="modal-backdrop show" />
        </>
      ) : null}

      <div className="row">
        {auctionModules.map((module) => {
          const moduleKey = module.id || module._id || module.title;
          return (
            <PageShell key={moduleKey} containerClassName="col-md-4">
              <AuctionModuleCard module={module} badge={module.id === 'online' ? <FeatureBadgeSlot featureKey="auction.online"/> : undefined} />
            </PageShell>
          );
        })}
      </div>

    </>
  );
}
export default AuctionManagementPage;
